import { useLocalization } from "@fluent/react"
import {
    Alert,
    Button,
    Chip,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Paper,
    Skeleton,
    Stack,
    Typography,
} from "@mui/material"
import DownloadIcon from "@mui/icons-material/Download"
import HistoryIcon from "@mui/icons-material/History"
import UploadFileIcon from "@mui/icons-material/UploadFile"
import { LoadingButton } from "@mui/lab"
import { unwrapResult } from "@reduxjs/toolkit"
import { useEffect, useState } from "react"
import { DeleteIconButton } from "../../../app/components/iconButtons/DeleteIconButton"
import { useAppDispatch } from "../../../app/hooks"
import { showError, showSuccess } from "../../notifications/notificationsSlice"
import { StructureNodeTreeDto } from "../structureSlice"
import {
    deleteReportTemplate,
    downloadReportTemplate,
    downloadSystemReportTemplate,
    loadReportTemplates,
    REPORT_DOCUMENT_TYPES,
    ReportTemplateDto,
    saveBlob,
    TemplateValidationReport,
} from "./reportTemplatesApi"
import { ReportTemplateVersionsDialog } from "./ReportTemplateVersionsDialog"
import { TemplateReportView } from "./TemplateReportView"
import { UploadNodeReportTemplateDialog } from "./UploadNodeReportTemplateDialog"

interface NodeReportTemplatesPanelProps {
    node: StructureNodeTreeDto
    isRoot: boolean
}

type DocumentTypeOption = typeof REPORT_DOCUMENT_TYPES[number]

export const NodeReportTemplatesPanel = ({ node, isRoot }: NodeReportTemplatesPanelProps) => {
    const { l10n } = useLocalization()
    const dispatch = useAppDispatch()

    const [templates, setTemplates] = useState<ReportTemplateDto[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [reloadTick, setReloadTick] = useState(0)

    const [uploadType, setUploadType] = useState<DocumentTypeOption | null>(null)
    const [historyFor, setHistoryFor] = useState<ReportTemplateDto | null>(null)
    const [deleteCandidate, setDeleteCandidate] = useState<ReportTemplateDto | null>(null)
    const [deleting, setDeleting] = useState(false)
    const [downloading, setDownloading] = useState<string | null>(null)
    // Warnings/fixes of the last upload, shown once under the card it concerns.
    const [lastReport, setLastReport] = useState<{ documentType: string, report: TemplateValidationReport } | null>(null)

    useEffect(() => {
        let active = true
        setIsLoading(true)
        setError(null)

        dispatch(loadReportTemplates())
            .then(unwrapResult)
            .then(data => {
                if (active) {
                    setTemplates(data)
                }
            })
            .catch(() => {
                if (active) {
                    setTemplates([])
                    setError(l10n.getString("report-template-load-error", null, "Nie udało się załadować szablonów raportów."))
                }
            })
            .finally(() => {
                if (active) {
                    setIsLoading(false)
                }
            })

        return () => {
            active = false
        }
        // The list is company-wide, so switching nodes only re-filters it.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, reloadTick])

    useEffect(() => {
        setLastReport(null)
    }, [node.id])

    const typeLabel = (type: DocumentTypeOption) => l10n.getString(type.labelId, null, type.labelFallback)

    const templateFor = (documentType: string) =>
        templates.find(t => t.structureNodeId === node.id && t.documentType === documentType) ?? null

    const replaceTemplate = (template: ReportTemplateDto) => {
        setTemplates(prev => [...prev.filter(t => t.id !== template.id), template])
    }

    const handleDownload = async (template: ReportTemplateDto) => {
        setDownloading(template.id)
        try {
            const result = await dispatch(downloadReportTemplate({ id: template.id }))
            if (downloadReportTemplate.fulfilled.match(result)) {
                saveBlob(result.payload, template.fileName || `${template.name}.docx`)
            } else {
                dispatch(showError("error"))
            }
        } finally {
            setDownloading(null)
        }
    }

    const handleDownloadSystem = async (type: DocumentTypeOption) => {
        setDownloading(type.value)
        try {
            const result = await dispatch(downloadSystemReportTemplate(type.value))
            if (downloadSystemReportTemplate.fulfilled.match(result)) {
                saveBlob(result.payload, `${type.value}_template.docx`)
            } else {
                dispatch(showError("error"))
            }
        } finally {
            setDownloading(null)
        }
    }

    const handleDelete = async () => {
        if (!deleteCandidate) {
            return
        }

        setDeleting(true)
        try {
            const result = await dispatch(deleteReportTemplate(deleteCandidate.id))
            if (deleteReportTemplate.fulfilled.match(result)) {
                setTemplates(prev => prev.filter(t => t.id !== deleteCandidate.id))
                if (lastReport?.documentType === deleteCandidate.documentType) {
                    setLastReport(null)
                }
                dispatch(showSuccess("report-template-deleted"))
            } else {
                dispatch(showError("error"))
            }
        } finally {
            setDeleting(false)
            setDeleteCandidate(null)
        }
    }

    const renderCard = (type: DocumentTypeOption) => {
        const template = templateFor(type.value)
        const report = lastReport?.documentType === type.value ? lastReport.report : null

        return (
            <Paper key={type.value} variant="outlined" sx={{ p: 1.25 }}>
                <Stack spacing={1}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                        <Typography variant="body1" sx={{ fontWeight: 700 }}>
                            {typeLabel(type)}
                        </Typography>
                        {template ? (
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                <Chip size="small" color="success" label={`v${template.currentVersion}`} />
                                <DeleteIconButton
                                    tooltip={l10n.getString("report-template-delete", null, "Usuń szablon")}
                                    onClick={() => setDeleteCandidate(template)}
                                />
                            </Stack>
                        ) : (
                            <Chip
                                size="small"
                                variant="outlined"
                                label={l10n.getString("report-template-builtin-in-use", null, "Szablon wbudowany")}
                            />
                        )}
                    </Stack>

                    {template ? (
                        <>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {template.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {l10n.getString(
                                    "report-template-uploaded-info",
                                    { by: template.uploadedBy, at: new Date(template.uploadedAt).toLocaleString() },
                                    `Przesłano: ${new Date(template.uploadedAt).toLocaleString()}, ${template.uploadedBy}`,
                                )}
                            </Typography>
                        </>
                    ) : (
                        <Typography variant="caption" color="text.secondary">
                            {l10n.getString(
                                "report-template-builtin-hint",
                                null,
                                "Brak własnego szablonu – raporty używają szablonu wbudowanego. Pobierz go jako punkt wyjścia.",
                            )}
                        </Typography>
                    )}

                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Button
                            size="small"
                            variant="contained"
                            startIcon={<UploadFileIcon />}
                            onClick={() => setUploadType(type)}
                        >
                            {template
                                ? l10n.getString("report-template-upload-version", null, "Nowa wersja")
                                : l10n.getString("report-template-upload", null, "Prześlij")}
                        </Button>
                        {template ? (
                            <>
                                <LoadingButton
                                    size="small"
                                    variant="outlined"
                                    startIcon={<DownloadIcon />}
                                    loading={downloading === template.id}
                                    onClick={() => { void handleDownload(template) }}
                                >
                                    {l10n.getString("report-template-download", null, "Download")}
                                </LoadingButton>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<HistoryIcon />}
                                    onClick={() => setHistoryFor(template)}
                                >
                                    {l10n.getString("report-template-history", null, "Historia")}
                                </Button>
                            </>
                        ) : (
                            <LoadingButton
                                size="small"
                                variant="outlined"
                                startIcon={<DownloadIcon />}
                                loading={downloading === type.value}
                                onClick={() => { void handleDownloadSystem(type) }}
                            >
                                {l10n.getString("report-template-download-builtin", null, "Pobierz wbudowany")}
                            </LoadingButton>
                        )}
                    </Stack>

                    <Collapse in={!!report && (report.warnings.length > 0 || report.fixes.length > 0)} unmountOnExit>
                        {report && <TemplateReportView report={report} showSuccess={false} />}
                    </Collapse>
                </Stack>
            </Paper>
        )
    }

    return (
        <Stack spacing={1.5}>
            {!isRoot && (
                <Alert severity="info">
                    {l10n.getString(
                        "report-template-non-root-hint",
                        null,
                        "Dokumenty nie są jeszcze powiązane z jednostkami – raporty korzystają z szablonów jednostki głównej. Szablony tej jednostki zaczną działać po wprowadzeniu tego powiązania.",
                    )}
                </Alert>
            )}

            {isLoading && (
                <Stack spacing={1}>
                    <Skeleton variant="rounded" height={110} />
                    <Skeleton variant="rounded" height={110} />
                </Stack>
            )}

            {!isLoading && error && (
                <Alert
                    severity="error"
                    action={
                        <Button color="inherit" size="small" onClick={() => setReloadTick(t => t + 1)}>
                            {l10n.getString("report-template-retry", null, "Ponów")}
                        </Button>
                    }
                >
                    {error}
                </Alert>
            )}

            {!isLoading && !error && REPORT_DOCUMENT_TYPES.map(renderCard)}

            {uploadType && (
                <UploadNodeReportTemplateDialog
                    documentType={uploadType.value}
                    documentTypeLabel={typeLabel(uploadType)}
                    node={node}
                    existing={templateFor(uploadType.value)}
                    onClose={() => setUploadType(null)}
                    onSaved={(template, report) => {
                        replaceTemplate(template)
                        setLastReport({ documentType: template.documentType, report })
                        setUploadType(null)
                        dispatch(showSuccess("report-template-uploaded"))
                    }}
                />
            )}

            {historyFor && (
                <ReportTemplateVersionsDialog
                    template={historyFor}
                    onClose={() => setHistoryFor(null)}
                    onChanged={template => {
                        replaceTemplate(template)
                        setHistoryFor(template)
                    }}
                />
            )}

            <Dialog open={!!deleteCandidate} maxWidth="sm" onClose={deleting ? undefined : () => setDeleteCandidate(null)}>
                <DialogTitle>
                    {l10n.getString("report-template-delete-title", null, "Usunąć szablon raportu?")}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {l10n.getString(
                            "report-template-delete-confirm",
                            { name: deleteCandidate?.name ?? "" },
                            `Szablon „${deleteCandidate?.name ?? ""}” zostanie usunięty, a raporty wrócą do szablonu wbudowanego.`,
                        )}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <LoadingButton loading={deleting} size="small" color="error" variant="contained" onClick={() => { void handleDelete() }}>
                        {l10n.getString("yes", null, "Tak")}
                    </LoadingButton>
                    <LoadingButton loading={deleting} size="small" variant="outlined" onClick={() => setDeleteCandidate(null)}>
                        {l10n.getString("no", null, "Nie")}
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </Stack>
    )
}
